<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { register, type SwiperContainer } from 'swiper/element/bundle'
import { useStore } from '@nanostores/vue'
import { useWindowScroll } from '@vueuse/core'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BackButton from '@/components/BackButton.vue'
import MercariCampaignCouponSection from '@/pages/mercari-campaign/components/_MercariCampaignCouponSection.vue'
import TabbedFlowSteps from '@/components/TabbedFlowSteps.vue'
import ShipmentProtectionFAQ from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFAQ.vue'
import { useBreakpoints, getVueAssetImagePath } from '@/lib/utils'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { isLogin } from '@/store/userStore'
import { isApp, isAppLogin } from '@/assets/ts/checkApp'
import {
	getSiteBaseMemberPath,
	getMcrRootUrl,
	getSiteBaseUrl,
} from '@/lib/siteBaseUrl'
import { fetchInappYahooLandingBanners } from '@/assets/ts/apiManagement'
import type { IMercariLandingBanner } from '@/types/Mercari'
import type { FAQContent } from '@/types/faq'

interface CouponConfig {
	type: 'bnshop' | 'shipment' | 'b4u'
	code: string
}

interface CouponSection {
	menuTitle: string
	title: string
	subtitle: string
	type: 'coupon' | 'image' | 'text'
	coupons: CouponConfig[]
}

interface AboutSection {
	title: string
	description: string
	buttonText: string
}

interface AdvantageItem {
	icon: string
	title: string
	description: string
}

interface AdvantagesSection {
	title: string
	items: AdvantageItem[]
}

interface FlowStep {
	title: string
	description: string
}

interface FlowTab {
	id: string
	title: string
	steps: FlowStep[]
}

interface FlowContent {
	sectionTitle: string
	buttonText?: string
	buttonLink?: string
	tabs: FlowTab[]
}

interface CouponCardText {
	coupon_card_amount: string
	coupon_card_percentage_off: string
	coupon_card_shipment_unit: string
	coupon_card_service_type_buyandshop_title: string
	coupon_card_service_type_buyforyou_title: string
	coupon_card_service_type_buyandship_title: string
	coupon_card_expiry_date_description: string
	coupon_card_copy: string
	coupon_card_copy_success: string
	coupon_details_title: string
	coupon_details_how_to_use: string
	coupon_details_terms_conditions: string
	coupon_details_product_express: string
	coupon_details_product_proxy: string
	coupon_details_product_shipping: string
}

interface Breadcrumbs {
	index: string
	shopping: string
	YahooLanding: string
}

interface YahooJpPageContent {
	breadcrumbs: Breadcrumbs
	about: AboutSection
	couponCardText: CouponCardText
	couponSection: CouponSection
	advantages: AdvantagesSection
	flowContent: FlowContent
	faq: FAQContent
}

const { isTabletLandscape } = useBreakpoints()
register()

// Use scroll position for back to top button
const { y: scrollY } = useWindowScroll()

// Show back button when scrolled past 33% of viewport height
const isShowBackToTopButton = computed(() => {
	return scrollY.value > window.innerHeight * 0.33
})

const scrollToTop = () => {
	window.scrollTo({
		top: 0,
		behavior: 'smooth',
	})
}

const props = defineProps<{
	pageContent: YahooJpPageContent
}>()

const isLoginGlobal = useStore(isLogin)
const landingBanners = ref<IMercariLandingBanner['banners']>([])
const isLandingBannersLoaded = ref(false)
const bannerSwiperEl = ref<SwiperContainer | null>(null)
const hasValidCoupons = ref(true)
const couponSectionRef = ref<InstanceType<
	typeof MercariCampaignCouponSection
> | null>(null)

// Flow icons for TabbedFlowSteps
const flowIcons = {
	proxy_shopping: [
		'flow_place_order',
		'flow_payment',
		'flow_consolidate',
		'flow_delivery',
	],
}

const prevSlide = () => {
	if (bannerSwiperEl.value) {
		bannerSwiperEl.value.swiper.slidePrev()
	}
}

const nextSlide = () => {
	if (bannerSwiperEl.value) {
		bannerSwiperEl.value.swiper.slideNext()
	}
}

const handleButtonClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.YahooLanding,
		button_name: 'Start Shopping',
	})

	if (isApp) {
		const buyforyouNewUrl = `${getSiteBaseUrl()}/account/buyforyou/new`
		if (isAppLogin) {
			window.location.href = buyforyouNewUrl
		} else {
			window.location.href = `${getSiteBaseUrl()}/login`
		}
		return
	}

	if (isLoginGlobal.value) {
		const buyforyouNewUrl = `${getMcrRootUrl()}/buyforyou/new`
		window.location.href = buyforyouNewUrl
	} else {
		const targetUrl = `${getSiteBaseMemberPath()}/buyforyou/new`

		const url = new URL(window.location.href)
		url.searchParams.set('redirect', targetUrl)
		window.history.pushState({}, '', url)

		window.BsHeaderVm.showPanel('login')
	}
}

const handleCouponsLoaded = (hasValidCouponsValue: boolean) => {
	hasValidCoupons.value = hasValidCouponsValue
}

const handleBannerClick = (bannerTitle: string) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.YahooLanding,
		button_name: 'Promotion Banner',
		campaign_name: bannerTitle,
		event_extra: bannerTitle,
	})
}

const setLandingBanners = async () => {
	try {
		const { banners } = await fetchInappYahooLandingBanners()
		landingBanners.value = banners
	} catch (error) {
		console.error('Failed to set Yahoo landing banners:', error)
	} finally {
		isLandingBannersLoaded.value = true
	}
}

onMounted(() => {
	setLandingBanners()
})
</script>

<template>
	<div class="flex flex-col gap-[32px] mdp:gap-[60px] mdp:pb-[60px]">
		<!-- Top Banner -->
		<section v-if="!isLandingBannersLoaded || landingBanners.length > 0">
			<div
				class="relative aspect-[4/3] bg-n-75 mdp:aspect-[3/1] lg:mx-auto lg:w-[1204px] lg:rounded-t-[8px]"
			>
				<swiper-container
					v-if="landingBanners.length > 0"
					ref="bannerSwiperEl"
					:loop="landingBanners.length > 1"
					slides-per-view="1"
					autoplay-delay="3000"
					class="h-full w-full lg:overflow-hidden lg:rounded-t-[8px]"
				>
					<swiper-slide v-for="(banner, index) in landingBanners" :key="index">
						<a :href="banner.link" @click="handleBannerClick(banner.title)">
							<img
								:src="
									!isTabletLandscape
										? banner.image.mobile
										: banner.image.desktop
								"
								:alt="banner.title || 'Marketing Campaign'"
								class="h-full w-full select-none object-cover"
								:loading="index ? 'lazy' : 'eager'"
							/>
						</a>
					</swiper-slide>
				</swiper-container>

				<BaseIcon
					v-if="landingBanners.length > 1"
					name="chevron_round"
					class="absolute left-[10px] top-1/2 z-[1] h-[40px] w-[40px] -translate-y-1/2 rotate-180 cursor-pointer mdp:left-[20px] lg:left-[-20px]"
					@click="prevSlide"
				/>

				<BaseIcon
					v-if="landingBanners.length > 1"
					name="chevron_round"
					class="absolute right-[10px] top-1/2 z-[1] h-[40px] w-[40px] -translate-y-1/2 cursor-pointer mdp:right-[20px] lg:right-[-20px]"
					@click="nextSlide"
				/>
			</div>
		</section>

		<!-- About Section -->
		<section v-if="pageContent.about" class="mdp:bs-container">
			<div
				class="relative col-span-12 bg-o-50 px-[24px] py-[32px] mdp:px-[150px] mdp:py-[48px]"
			>
				<picture>
					<source
						:srcset="getVueAssetImagePath('yahoo-jp-about-bg.avif')"
						type="image/avif"
					/>
					<img
						class="absolute left-0 top-0 z-0 hidden h-full w-full object-cover object-center mdp:block"
						:src="getVueAssetImagePath('yahoo-jp-about-bg.png')"
						loading="lazy"
						alt="Yahoo Japan About banner"
					/>
				</picture>
				<div
					class="relative flex flex-col items-center gap-[32px] text-center mdp:gap-close-ish-friends"
				>
					<h3 v-if="pageContent.about.title" class="typography-h3-str">
						{{ pageContent.about.title }}
					</h3>
					<p
						v-if="pageContent.about.description"
						class="typography-s1-reg text-left text-n-500"
					>
						{{ pageContent.about.description }}
					</p>
					<BaseButton
						v-if="pageContent.about.buttonText"
						variant="primary"
						size="large"
						@click="handleButtonClick"
					>
						{{ pageContent.about.buttonText }}
					</BaseButton>
				</div>
			</div>
		</section>

		<!--
		Coupon display rules:
		- The coupons shown in couponSection must match both type and code between:
		  1. Page content's couponSection.coupons
		  2. API's coupon_list

		Text display rules:
		- The text shown on coupon cards comes from page content's couponCardText
		-->

		<!-- Coupon Section -->
		<section v-show="hasValidCoupons" class="bs-container">
			<div
				class="col-span-12 py-[24px] text-center [&_h4]:text-left [&_h4]:mdp:text-center"
			>
				<MercariCampaignCouponSection
					ref="couponSectionRef"
					:section="pageContent.couponSection"
					:coupon-card-text="pageContent.couponCardText"
					@coupons-loaded="handleCouponsLoaded"
				/>
			</div>
		</section>

		<!-- Buy&Ship 3 Key Advantages -->
		<section v-if="pageContent.advantages" class="mdp:bs-container">
			<div
				class="col-span-12 flex flex-col items-center gap-[32px] rounded-lg bg-t-50 px-[24px] py-[32px] mdp:flex-row mdp:items-start mdp:p-[48px]"
			>
				<h3
					class="typography-h3-str text-center mdp:min-w-[350px] mdp:text-left"
				>
					{{ pageContent.advantages.title }}
				</h3>
				<div class="flex flex-col gap-32px">
					<div
						class="flex mdp:flex-col mdp:gap-32px"
						v-for="(item, index) in pageContent.advantages.items"
						:key="index"
					>
						<div class="flex flex-col gap-12px">
							<div class="flex items-center gap-12px">
								<BaseIcon :name="item.icon" class="h-24px w-24px fill-t-700" />
								<h5 class="typography-h5-str">{{ item.title }}</h5>
							</div>
							<div class="typography-s1-reg text-n-500">
								{{ item.description }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- flowContent -->
		<section v-if="props.pageContent.flowContent" class="bs-container">
			<div class="col-span-12">
				<TabbedFlowSteps
					:content="props.pageContent.flowContent"
					:flow-icons="flowIcons"
				/>
			</div>
		</section>

		<!-- FAQ section -->
		<ShipmentProtectionFAQ
			v-if="pageContent.faq"
			:content="pageContent.faq"
			theme="yahoo-jp"
			variant="contained"
		/>

		<!-- Back to top button -->
		<!-- Back to top button positioned above Intercom launcher (48px height + 20px offset + 20px bottom margin = 88px) -->
		<div
			v-show="isShowBackToTopButton"
			class="fixed bottom-20px right-20px z-50 mdp:bottom-[88px] mdp:right-20px"
		>
			<BackButton @click="scrollToTop" />
		</div>
	</div>
</template>
